// The RETIRED localStorage keys that used to hold the user's active-tenant
// selection, plus the one clearing sequence every sign-out path shares.
//
// BL-1109 removed the client-side copy entirely. The active tenant is now
// resolved SERVER-side from `staff_users.last_active_tenant_id` — the column the
// tenant switcher has always written on an explicit switch — so there is no
// second, client-held source of truth to drift from it. Nothing reads or writes
// these keys any more.
//
// They survive only as a sweep: `clearActiveTenantKeys` still removes them on
// every sign-out so a stale value left by a pre-BL-1109 build doesn't linger in
// a browser. Delete this module once that's no longer worth carrying.
//
// Storing it server-side also closes BUG-704's shared-device hazard by
// construction rather than by discipline: the column is keyed on
// `staff_users.id`, so a device can't carry one operator's tenant into the next
// operator's session — the next session reads its own row.
//
// Versioned on purpose. An earlier build had a TenantSelector fallback effect
// that, during the brief null-currentTenant window on a hard refresh,
// automatically selected `userTenants[0]` (the alphabetical-first tenant —
// "Acme") and persisted it through the localStorage-writing setter. That
// poisoned the stored value with a tenant the user never chose, and because
// localStorage outranks the server-side default, the poison kept winning on
// every reload (BUG-704).
//
// Bumping the key suffix abandons every poisoned legacy value in one shot: on
// the first load after deploy the new key is absent, so selection falls through
// to the server-resolved default (`isDefault`, backed by users.last_active_tenant_id).
// Going forward ONLY an explicit user switch writes this key — automatic/initial
// selection never does — so it can't be poisoned again.
export const ACTIVE_TENANT_STORAGE_KEY = 'activeTenantId.v2';

// Older keys that may still hold poisoned values. Cleared on sign-out so they
// don't linger in the browser; never read.
export const LEGACY_ACTIVE_TENANT_STORAGE_KEYS = ['selectedTenantId'] as const;

/**
 * Sweep every retired active-tenant localStorage key off this device. Called on
 * EVERY sign-out path — the main `SessionProvider.signOut` and the standalone
 * page sign-outs (`/auth/unauthorized`, `/auth/complete-signup`) — and on a
 * resolved-identity change. Shared here (the keys' home) so the sequence can't
 * drift between call sites.
 *
 * Post-BL-1109 this is hygiene, not correctness: nothing reads these keys, and
 * the active tenant lives on the user's own `staff_users` row, so a leftover
 * value can no longer influence anyone's session.
 *
 * @sideEffect Removes the retired localStorage keys.
 */
export function clearActiveTenantKeys(): void {
    localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    for (const legacyKey of LEGACY_ACTIVE_TENANT_STORAGE_KEYS) {
        localStorage.removeItem(legacyKey);
    }
}
